<template>
    <BaseModal
        :title="$t('route_optimization.create_order')"
        :hide-footer="true"
        size="lg"
        @close="$emit('close')"
    >
        <div class="create-orders-modal">
            <div
                v-if="loadingJob"
                class="text-center py-4"
            >
                <div class="spinner-border spinner-border-sm text-primary" />
            </div>

            <template v-else-if="totalSteps > 0">
                <!-- Step indicator -->
                <div class="mb-2 text-muted small fw-bold">
                    {{ $t('route_optimization.step_of', { current: currentStep + 1, total: totalSteps }) }}
                </div>

                <!-- Progress bar -->
                <div
                    class="progress mb-3"
                    style="height: 4px;"
                >
                    <div
                        class="progress-bar"
                        role="progressbar"
                        :style="{ width: ((currentStep + 1) / totalSteps * 100) + '%' }"
                    />
                </div>

                <!-- Two-column layout: map + form -->
                <div class="col gx-3">
                    <!-- Map preview -->
                    <div class="col-12 mb-3 mb-md-0">
                        <span class="text-muted small fw-bold">
                            {{ $t("route_optimization.geometry_preview") }}
                        </span>

                        <div
                            ref="mapContainer"
                            class="route-preview-map"
                        />
                    </div>

                    <!-- Form -->
                    <div class="col-12">
                        <div
                            v-if="errorMessage"
                            class="alert alert-danger py-2"
                        >
                            {{ errorMessage }}
                        </div>

                        <div class="mb-2">
                            <label class="form-label">{{ $t('orders.contract') }} *</label>

                            <multiselect
                                v-model="currentOrder.contract"
                                :options="contracts"
                                :close-on-select="true"
                                label="name"
                                track-by="id"
                                :searchable="true"
                                :internal-search="true"
                                :selected-label="$t('common.selected')"
                                :select-label="$t('common.select')"
                                :deselect-label="$t('common.remove_selection')"
                                :placeholder="$t('contracts.select_contract')"
                                :disabled="loadingContracts"
                                :loading="loadingContracts"
                                @select="onContractChange"
                                @remove="onContractChange(null)"
                            >
                                <template #singleLabel="{ option }">
                                    <span>{{ option.customer.name }} - {{ option.name }}</span>
                                </template>

                                <template #option="{ option }">
                                    <div>{{ option.customer.name }} - {{ option.name }}</div>
                                </template>

                                <template #noResult>
                                    <span>{{ $t('common.not_found') }}</span>
                                </template>

                                <template #noOptions>
                                    <span>{{ $t('common.no_results') }}</span>
                                </template>
                            </multiselect>
                        </div>

                        <div class="mb-2">
                            <label class="form-label">{{ $t('orders.task_type') }} *</label>

                            <select
                                v-model="currentOrder.contractTaskTypeId"
                                class="form-select form-select-sm"
                                :disabled="!currentOrder.contract || loadingTaskTypes"
                            >
                                <option
                                    :value="null"
                                    disabled
                                >
                                    {{ $t('route_optimization.select_contract_task_type') }}
                                </option>

                                <option
                                    v-for="ct in contractTaskTypes"
                                    :key="ct.id"
                                    :value="ct.id"
                                >
                                    {{ ct.task_type.name }}
                                </option>
                            </select>
                        </div>

                        <div class="mb-2">
                            <label class="form-label">{{ $t('orders.executor') }}</label>

                            <multiselect
                                v-model="currentOrder.executorIds"
                                :options="executors"
                                :multiple="true"
                                :close-on-select="false"
                                label="name"
                                track-by="id"
                                :searchable="true"
                                :internal-search="true"
                                :selected-label="$t('common.selected')"
                                :select-label="$t('common.select')"
                                :deselect-label="$t('common.remove_selection')"
                                :placeholder="$t('orders.select_executor')"
                                :disabled="loadingExecutors || !currentOrder.contract"
                                :loading="loadingExecutors"
                            >
                                <template #noResult>
                                    <span>{{ $t('common.not_found') }}</span>
                                </template>

                                <template #noOptions>
                                    <span>{{ $t('common.no_results') }}</span>
                                </template>
                            </multiselect>
                        </div>

                        <div class="mb-2">
                            <label class="form-label">{{ $t('orders.info') }}</label>

                            <textarea
                                v-model="currentOrder.info"
                                type="text"
                                rows="5"
                                class="form-control form-control-sm"
                                :placeholder="$t('orders.info')"
                            />
                        </div>

                        <div class="mb-2">
                            <label class="form-label">{{ $t('orders.planned_start') }}</label>

                            <DateTimePicker
                                :date="currentOrder.plannedStart"
                                :enable-time-picker="true"
                                @update:date="currentOrder.plannedStart = $event"
                            />
                        </div>

                        <div class="mb-2">
                            <label class="form-label">{{ $t('orders.planned_ready') }}</label>

                            <DateTimePicker
                                :date="currentOrder.plannedReady"
                                :enable-time-picker="true"
                                @update:date="currentOrder.plannedReady = $event"
                            />
                        </div>
                    </div>
                </div>

                <!-- Navigation buttons -->
                <div class="d-flex justify-content-between mt-3">
                    <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        :disabled="currentStep === 0"
                        @click="goBack"
                    >
                        {{ $t('route_optimization.back') }}
                    </button>

                    <div class="d-flex gap-2">
                        <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm"
                            @click="$emit('close')"
                        >
                            {{ $t('common.cancel') }}
                        </button>

                        <button
                            v-if="currentStep < totalSteps - 1"
                            type="button"
                            class="btn btn-primary btn-sm"
                            :disabled="!currentOrder.contractTaskTypeId"
                            @click="goNext"
                        >
                            {{ $t('route_optimization.next') }}
                        </button>

                        <button
                            v-else
                            type="button"
                            class="btn btn-primary btn-sm"
                            :disabled="!currentOrder.contractTaskTypeId || submitting"
                            @click="submit"
                        >
                            <span
                                v-if="submitting"
                                class="spinner-border spinner-border-sm me-1"
                            />
                            {{ $t('route_optimization.create_order') }}
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </BaseModal>
</template>

<script>
import { inject } from 'vue'
import L from 'leaflet'
import BaseModal from '@/components/BaseModal.vue'
import DateTimePicker from '@/components/common/DateTimePicker.vue'
import { restApi } from '../mixins/RestApiMixin'

const VEHICLE_COLORS = [
    '#7FC200', '#2196F3', '#FF8C00', '#FF6B6B', '#4ECDC4',
    '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'
]

export default {
    name: 'CreateOrdersFromJobModal',
    components: { BaseModal, DateTimePicker },
    mixins: [restApi],
    props: {
        job: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'created'],
    setup() {
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    data() {
        return {
            loadingJob: true,
            totalSteps: 0,
            currentStep: 0,
            orders: [],
            routeData: [],
            contracts: [],
            contractTaskTypes: [],
            executors: [],
            loadingContracts: false,
            loadingTaskTypes: false,
            loadingExecutors: false,
            submitting: false,
            errorMessage: null,
            map: null,
            routeLayers: [],
        }
    },
    computed: {
        currentOrder() {
            return this.orders[this.currentStep] || {}
        }
    },
    watch: {
        currentStep() {
            this.errorMessage = null
            this.loadTaskTypesForCurrentStep()
            this.highlightCurrentRoute()
        }
    },
    mounted() {
        this.fetchJobDetails()
        this.fetchContracts()
    },
    beforeUnmount() {
        if (this.map) {
            this.map.remove()
            this.map = null
        }
    },
    methods: {
        fetchJobDetails() {
            this.loadingJob = true

            this.restFetch(`${this.routeOptimizationJobUrl}/${this.job.job_id}`, this.jobFetchSuccess, this.jobFetchFailed)
        },
        jobFetchSuccess(response) {
            const data = response.data

            if (data && data.result_data && data.result_data.length > 0) {
                this.routeData = data.result_data

                this.totalSteps = data.result_data.length

                this.orders = data.result_data.map((route, index) => ({
                    routeIndex: index,
                    contract: null,
                    contractTaskTypeId: null,
                    executorIds: [],
                    info: route.metadata?.vehicleName || '',
                    plannedStart: null,
                    plannedReady: null,
                }))
            }
            this.loadingJob = false

            this.$nextTick(() => {
                // Use setTimeout to ensure teleported modal DOM is fully rendered
                setTimeout(() => {
                    this.initMap()
                }, 100)
            })
        },
        jobFetchFailed() {
            this.loadingJob = false
            this.errorMessage = this.$t('route_optimization.create_order_error')
        },
        initMap() {
            if (!this.$refs.mapContainer || this.map) return

            this.map = L.map(this.$refs.mapContainer, {
                zoomControl: true,
                attributionControl: false,
            })

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
            }).addTo(this.map)

            setTimeout(() => {
                this.map.invalidateSize()

                this.renderAllRoutes()

                this.highlightCurrentRoute()
            }, 50)
        },
        renderAllRoutes() {
            if (!this.map) return

            this.routeLayers.forEach(layer => this.map.removeLayer(layer))

            this.routeLayers = []

            this.routeData.forEach((route, index) => {
                const geojson = route.geojson || route

                if (!geojson || !geojson.features) return

                const color = VEHICLE_COLORS[index % VEHICLE_COLORS.length]

                const layer = L.geoJSON(geojson, {
                    style: {
                        color: color,
                        weight: 3,
                        opacity: 0.25,
                    },
                    pointToLayer: () => null,
                })

                layer.addTo(this.map)

                this.routeLayers.push(layer)
            })

            this.fitAllRoutes()
        },
        highlightCurrentRoute() {
            if (!this.map || this.routeLayers.length === 0) return

            this.routeLayers.forEach((layer, index) => {
                const isActive = index === this.currentStep

                layer.setStyle({
                    opacity: isActive ? 1 : 0.2,
                    weight: isActive ? 5 : 2,
                })
            })

            // Fit bounds to current route
            const activeLayer = this.routeLayers[this.currentStep]

            if (activeLayer) {
                const bounds = activeLayer.getBounds()

                if (bounds.isValid()) {
                    this.map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 })
                }
            }
        },
        fitAllRoutes() {
            if (!this.map || this.routeLayers.length === 0) return

            const group = L.featureGroup(this.routeLayers)

            const bounds = group.getBounds()

            if (bounds.isValid()) {
                this.map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 })
            }
        },
        fetchContracts() {
            this.loadingContracts = true

            const params = { startDate: new Date().toISOString() }

            this.restFetchParams(this.contractOpenUrl, params, (response) => {
                this.loadingContracts = false

                if (response && response.data) {
                    this.contracts = response.data
                }
            })
        },
        fetchExecutors(contractId) {
            this.loadingExecutors = true

            this.restFetchParams(this.contractCompanyUrl, { contract: contractId }, (response) => {
                this.loadingExecutors = false

                if (response && response.data) {
                    this.executors = response.data
                }
            })
        },
        onContractChange(contract) {
            this.currentOrder.contract = contract
            this.currentOrder.contractTaskTypeId = null
            this.currentOrder.executorIds = []
            this.contractTaskTypes = []
            this.executors = []

            if (contract) {
                this.fetchContractTaskTypes(contract.id)
                this.fetchExecutors(contract.id)
            }
        },
        fetchContractTaskTypes(contractId) {
            this.loadingTaskTypes = true

            this.restFetchParams(this.contractTaskTypeUrl, { contract: contractId }, (response) => {
                this.loadingTaskTypes = false
                if (response && response.data) {
                    this.contractTaskTypes = response.data
                }
            })
        },
        loadTaskTypesForCurrentStep() {
            const order = this.orders[this.currentStep]

            if (order && order.contract) {
                this.fetchContractTaskTypes(order.contract.id)
                this.fetchExecutors(order.contract.id)
            } else {
                this.contractTaskTypes = []
                this.executors = []
            }
        },
        goNext() {
            if (!this.currentOrder.contractTaskTypeId) return

            if (this.currentStep < this.totalSteps - 1) {
                // Pre-fill contract and task type to all unfilled subsequent steps
                for (let i = this.currentStep + 1; i < this.orders.length; i++) {
                    if (!this.orders[i].contract) {
                        this.orders[i].contract = this.currentOrder.contract
                        this.orders[i].contractTaskTypeId = this.currentOrder.contractTaskTypeId
                        this.orders[i].executorIds = [...this.currentOrder.executorIds]
                    }
                }
                this.currentStep++
            }
        },
        goBack() {
            if (this.currentStep > 0) {
                this.currentStep--
            }
        },
        submit() {
            // Validate all steps have required field
            for (let i = 0; i < this.orders.length; i++) {
                if (!this.orders[i].contractTaskTypeId) {
                    this.currentStep = i

                    this.errorMessage = this.$t('route_optimization.select_contract_task_type')
                    return
                }
            }

            this.submitting = true
            this.errorMessage = null

            const payload = {
                jobId: this.job.job_id,

                orders: this.orders.map(order => {
                    const entry = {
                        routeIndex: order.routeIndex,
                        contractTaskTypeId: order.contractTaskTypeId,
                    }

                    if (order.executorIds && order.executorIds.length > 0) {
                        entry.executorIds = order.executorIds.map(ex => ex.id)
                    }

                    if (order.info && order.info.trim()) {
                        entry.info = order.info.trim()
                    }

                    if (order.plannedStart) {
                        entry.plannedStart = order.plannedStart
                    }

                    if (order.plannedReady) {
                        entry.plannedReady = order.plannedReady
                    }

                    return entry
                })
            }

            this.restAdd(this.orderRoutesUrl, payload, this.createOrderSuccess, this.createOrderFail)
        },
        createOrderSuccess() {
            this.submitting = false

            this.showAlert(this.$t('route_optimization.create_order_success'), 'success')

            this.$emit('created')
        },
        createOrderFail(err) {
            this.submitting = false

            if (err && err.data && err.data.message) {
                this.errorMessage = err.data.message
            } else {
                this.errorMessage = this.$t('route_optimization.create_order_error')
            }
        }
    }
}
</script>

<style scoped>
.create-orders-modal {
    padding: 0.5em;
}

.form-label {
    font-size: 0.85em;
    color: #404041;
    margin-bottom: 0.15rem;
}

.progress-bar {
    background-color: #7FC000;
    transition: width 0.3s ease;
}

.route-preview-map {
    width: 100%;
    height: 300px;
    border-radius: 0.5em;
    border: 1px solid #dee2e6;
    z-index: 0;
}

@media (min-width: 768px) {
    .route-preview-map {
        height: 100%;
        min-height: 380px;
    }
}
</style>
